<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRegisterEntryPartnerSelector',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    editor: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onOptionSelected = (...args) => {
    const [{ option, value }] = args;
    if (_.isString(option)) {
      props.editor.partner = { name: option };
    }
  };

  const messages = ref({
    searchPlaceholder: $fn.value.pgettext('Register', 'Search for partner'),
  });
  const ux = ref([]);
  const b_2248226175642056_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      exclude: [props.editor.version.company_id],
    },
  }));
  const b_2248226175642056_optionTemplate = computed(
    () =>
      '<div>\n\
  <span v-if="!option.id">{{ option.name }}</span>\n\
  <UcSuggestCompanyOption2 v-if="option.id" :option=option></UcSuggestCompanyOption2>\n\
</div>'
  );

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
    b_2248226175642056_dataSource: b_2248226175642056_dataSource.value,
    b_2248226175642056_optionTemplate: b_2248226175642056_optionTemplate.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_2248226175642056_dataSource', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_optionTemplate', nodeId: '2248226175642056' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      messages,
      ux,
      b_2248226175642056_dataSource,
      b_2248226175642056_optionTemplate,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.editor,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editor');
    }
  );

  const act_2248226175642056_script_el0lcv24o_value_callable = async (
    $event
  ) => {
    return onOptionSelected($event.data);
  };
  const act_2248226175642056_script_el0lcv24o_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_el0lcv24o_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_x7b5xf20y =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_26czaw225 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_x7b5xf20y,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_26czaw225],
    event: 'reload',
  });
  const act_2248226175642056_script_el0lcv24o = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_el0lcv24o_getActionArgs,
  });
  const inst_evh_2248226175642056_optionSelected_603o1fprl =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_el0lcv24o,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_2248226175642056_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_optionSelected_603o1fprl],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_26czaw225,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_optionSelected_603o1fprl,
    inst_evh_2248226175642056_optionSelected,
  };

  const evh_7315092382398562_reload_26czaw225 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_26czaw225.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_optionSelected_603o1fprl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_optionSelected_603o1fprl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_optionSelected = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpSelect2
      name="partner"
      v-model="editor.partner"
      :value="editor.partner"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (editor.partner =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
      bind-object
      taggable
      :dataSource="b_2248226175642056_dataSource"
      valueField="id"
      textField="name"
      clearable
      :optionTemplate="b_2248226175642056_optionTemplate"
      use-option-template-for-selected-option
      :readonly="editor.isReadonly"
      @optionSelected="evh_2248226175642056_optionSelected($event, {})"
    >
    </ItpSelect2>
  </ItpUsercontrol>
</template>
